/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */

import React from 'react';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import DeviceDetailsGridItem, {DeviceDetailsGridItemProps} from './DeviceDetailsGridItem';

export interface DeviceDetailsFavoritedItemProps extends DeviceDetailsGridItemProps {
  id: string;
  widthOverride?: number;
}

/**
 * A wrapper around `DeviceDetailsGridItem` to make a field drag-and-droppable.
 * Used in favorited items section of the Device Details tab.
 * 
 * ```typescript
 * 
 *   import DeviceDetailsFavoritedItem from './DeviceDetailsFavoritedItem';
 * 
 *   // Provide a unique ID and a width if the item is outside of a grid container
 *   const favoritedItem = <DeviceDetailsFavoritedItem {...props}
 *     id={id}
 *     widthOverride={isDragging ? draggingItemWidth : undefined} />;
 * 
 * ```
 * 
 * @param id            An identifier that is unique within this field's
 *                      drag-and-drop context.
 * @param widthOverride Number of pixels to force this field's width to be.
 *                      Used primarily when this component is rendered outside
 *                      of any grid container.
 * @param ...props      Any remaining props are forwarded to
 *                      `DeviceDetailsGridItem`.
 */
const DeviceDetailsFavoritedItem = ({
  id,
  widthOverride = null,
  ...props
}: DeviceDetailsFavoritedItemProps) => {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({
    animateLayoutChanges : () => false,
    id
  });

  const style = {
    transform : CSS.Translate.toString(transform),
    opacity   : isDragging ? 0.5 : 1,
    transition,
    ...(widthOverride && {minWidth: widthOverride})
  };

  return <DeviceDetailsGridItem {...props} sortableProps={{
    attributes,
    listeners,
    setNodeRef,
    style
  }} />;
};

export default DeviceDetailsFavoritedItem;
